import React from "react";
import styled from "styled-components";
import BasicButton from "../BasicButton/BasicButton";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { createPortal } from "react-dom";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 1rem 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0.5rem;
  max-width: 258px;
  min-width: 258px;
  min-height: 134px;

  border: 1px dashed var(--color-sky-200);
  border-radius: 0.5rem;
`;

const ContainerAndLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ContainerLabel = styled.span`
  font-size: 1rem;
  line-height: 1;
  text-align: left;
  color: var(--color-sky-50);
`;

const FaceWrapper = styled.button`
  all: unset;
  width: 50px;
  height: 50px;
  border-radius: 0.25rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: grab;
  background-color: var(--background-color);
  touch-action: none;
  border-radius: 4px;
  border: 2px solid var(--color-sky-100);
`;

const FaceImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 4px;
`;

function Face({ id, src, img }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transition,
    transform,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition,
    // "--background-color": color,
    zIndex: isDragging ? 1 : 0,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <FaceWrapper ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <FaceImage src={src} alt={id} />
    </FaceWrapper>
  );
}

function DroppableContainer({ id, children }) {
  const { setNodeRef } = useDroppable({ id });

  return <Container ref={setNodeRef}>{children}</Container>;
}

function FaceOverlay({ src, img }) {
  return (
    <FaceWrapper style={{ opacity: 0.6, zIndex: 1000 }}>
      <FaceImage src={src} alt={src} />
    </FaceWrapper>
  );
}

function DraggableFaces({
  usedFaces,
  setUsedFaces,
  unusedFaces,
  setUnusedFaces,
}) {
  //   const [usedFaces, setUsedFaces] = React.useState([
  //     { id: "1", color: "#e74c3c" },
  //     { id: "2", color: "#3498db" },
  //     { id: "3", color: "#2ecc71" },
  //     { id: "4", color: "#f39c12" },
  //   ]);

  //   const [unusedFaces, setUnusedFaces] = React.useState([
  //     { id: "5", color: "#9b59b6" },
  //   ]);

  const [activeId, setActiveId] = React.useState(null);
  const sensors = useSensors(useSensor(PointerSensor));

  function findContainer(id) {
    if (usedFaces.some((item) => item.id === id)) return "used";
    if (unusedFaces.some((item) => item.id === id)) return "unused";
    return null;
  }

  function findItem(id) {
    return (
      usedFaces.find((item) => item.id === id) ||
      unusedFaces.find((item) => item.id === id)
    );
  }

  function handleDragStart(event) {
    setActiveId(event.active.id);
  }

  function handleDragOver(event) {
    const { active, over } = event;
    if (!over) return;

    const activeContainer = findContainer(active.id);

    let overContainer = findContainer(over.id);
    if (!overContainer && (over.id === "used" || over.id === "unused")) {
      overContainer = over.id;
    }

    // Only handle cross-container moves here
    if (
      !activeContainer ||
      !overContainer ||
      activeContainer === overContainer
    ) {
      return;
    }

    const activeItem = findItem(active.id);

    // Figure out the insertion index
    const overItems = overContainer === "used" ? usedFaces : unusedFaces;
    const overIndex = overItems.findIndex((i) => i.id === over.id);

    // If over.id is an item, insert at that index; otherwise append
    const newIndex = overIndex >= 0 ? overIndex : overItems.length;

    if (activeContainer === "used") {
      setUsedFaces((items) => items.filter((i) => i.id !== active.id));
      setUnusedFaces((items) => [
        ...items.slice(0, newIndex),
        activeItem,
        ...items.slice(newIndex),
      ]);
    } else {
      setUnusedFaces((items) => items.filter((i) => i.id !== active.id));
      setUsedFaces((items) => [
        ...items.slice(0, newIndex),
        activeItem,
        ...items.slice(newIndex),
      ]);
    }
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(over.id);

    // Only handle same-container reordering here
    if (
      activeContainer &&
      activeContainer === overContainer &&
      active.id !== over.id
    ) {
      const setItems =
        activeContainer === "used" ? setUsedFaces : setUnusedFaces;

      setItems((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  const activeItem = activeId ? findItem(activeId) : null;

  function shuffle(array) {
    let currentIndex = array.length;

    // While there remain elements to shuffle...
    while (currentIndex !== 0) {
      // Pick a remaining element...
      let randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ];
    }
  }

  function handleShuffle() {
    let all = [...usedFaces, ...unusedFaces];
    shuffle(all);
    const allUsedNew = [all[0]];
    const allUnusedNew = [];
    all = all.slice(1);

    for (let idx = 0; idx < all.length; idx++) {
      const face = all[idx];
      if (Math.random() < 0.5) {
        allUsedNew.push(face);
      } else {
        allUnusedNew.push(face);
      }
    }

    setUsedFaces(allUsedNew);
    setUnusedFaces(allUnusedNew);

    // setUsedFaces(() => {
    //   const newFaces = [...usedFaces];
    //   shuffle(newFaces);
    //   return newFaces;
    // });
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Wrapper>
        <ContainerAndLabel>
          {/* <LabelAndShuffle> */}
          <ContainerLabel>Brians (Active)</ContainerLabel>
          {/* </LabelAndShuffle> */}
          {/* Each SortableContext needs its own items array and a unique id */}
          <SortableContext items={usedFaces.map((item) => item.id)}>
            <DroppableContainer id="used">
              {usedFaces.map((item) => (
                <Face key={item.id} {...item} />
              ))}
            </DroppableContainer>
          </SortableContext>
        </ContainerAndLabel>

        <ContainerAndLabel>
          <ContainerLabel>Brians (Benched)</ContainerLabel>
          <SortableContext items={unusedFaces.map((item) => item.id)}>
            <DroppableContainer id="unused">
              {unusedFaces.map((item) => (
                <Face key={item.id} {...item} />
              ))}
            </DroppableContainer>
          </SortableContext>
        </ContainerAndLabel>
        <BasicButton onClick={handleShuffle}>Shuffle Brians</BasicButton>
      </Wrapper>

      {/* DragOverlay renders a floating copy of the dragged item */}
      {createPortal(
        <DragOverlay dropAnimaton={null}>
          {activeItem ? (
            <FaceOverlay src={activeItem.src} img={activeItem.img} />
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
}

export default DraggableFaces;
